import type { QuestionType } from '@l2u/ui/lib/types.ts';

// The question builder's AI save gate, extracted as pure functions (pattern:
// categoryRemoval.ts / documentRow.ts — the app has no DOM test env).
//
// Mirrors the server's crossFieldChecks: an AI-enabled question with zero
// criteria has nothing to judge against (the verifier contract requires at
// least one criterion), so Save is blocked with an inline error instead of the
// admin discovering at review time that nothing ran.

export type AiDraftFields = {
  type: QuestionType;
  allow_upload: boolean;
  requires_upload: boolean;
  ai_enabled: boolean;
  ai_criteria: string[];
};

/**
 * Whether a question of this shape can carry a vendor file — the only
 * questions AI analysis applies to. Shared with toApiPayload so the payload
 * and the save gate can never disagree about which drafts have live AI fields.
 */
export function canCarryFile(
  d: Pick<AiDraftFields, 'type' | 'allow_upload' | 'requires_upload'>,
): boolean {
  // toApiPayload forces the upload flags off for these types, so a stale
  // allow_upload left in the draft after a type switch must not count.
  const suppressUpload =
    d.type === 'document_adopt' ||
    d.type === 'signature' ||
    d.type === 'informational' ||
    d.type === 'image_info';
  return (
    d.type === 'document_upload' ||
    d.type === 'multiselect_uploads' ||
    (!suppressUpload && (d.allow_upload || d.requires_upload))
  );
}

/** null = save allowed; otherwise the inline error message blocking Save. */
export function aiSaveError(d: AiDraftFields): string | null {
  // toApiPayload strips the AI fields for non-file questions, so an enabled
  // box left behind by a type switch is harmless — never block on it.
  if (!canCarryFile(d)) return null;
  if (!d.ai_enabled) return null;
  const criteria = d.ai_criteria.map((s) => s.trim()).filter(Boolean);
  if (criteria.length === 0) {
    return 'AI analysis is enabled but has no criteria — add at least one criterion or untick "Analyse uploads with AI".';
  }
  return null;
}
